// FILE: src/components/SecondaryDimensionCard.js
//
// Dashboard coaching-rail panel showing the learner's strongest + weakest
// values for a cert's SECONDARY metadata dimension (e.g. PMI-PMOCP "Focus
// Areas"). Config-driven: rendered ONLY when getSecondaryDimension(examId) is
// non-null. Certs without a secondary dimension never mount this card.
//
// Visual language mirrors FocusAreasCard (accuracy bands: red <60, amber
// 60-74, green 75+).

import React from 'react';
import styles from './SecondaryDimensionCard.module.css';
import { getSecondaryDimension } from '../../config/exams';

export default function SecondaryDimensionCard({ examId, performance, onViewDetails }) {
  const dimension = getSecondaryDimension(examId);
  // Hard gate: no secondary dimension → render nothing at all.
  if (!dimension) return null;

  const strongest = (performance && performance.strongest) || [];
  const weakest   = (performance && performance.weakest)   || [];
  const label     = dimension.label || 'Focus Areas';

  if (strongest.length === 0 && weakest.length === 0) {
    return (
      <div className={styles.card} data-testid="dashboard-secondary-dimension">
        <div className={styles.head}>◎ {label}</div>
        <div className={styles.empty}>
          Not enough {label.toLowerCase()} data yet — keep practicing to unlock this breakdown.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card} data-testid="dashboard-secondary-dimension">
      <div className={styles.head}>◎ {label}</div>

      {weakest.length > 0 && (
        <div className={styles.group}>
          <div className={styles.groupLabel}>Needs work</div>
          {weakest.map((w) => <Row key={`w-${w.area}`} item={w} />)}
        </div>
      )}

      {strongest.length > 0 && (
        <div className={styles.group}>
          <div className={styles.groupLabel}>Strongest</div>
          {strongest.map((w) => <Row key={`s-${w.area}`} item={w} />)}
        </div>
      )}

      {onViewDetails && (
        <button type="button" className={styles.link} onClick={onViewDetails}>
          View details ›
        </button>
      )}
    </div>
  );
}

function Row({ item }) {
  const acc = Math.round(item.accuracy || 0);
  return (
    <div className={styles.row}>
      <div className={styles.rowHead}>
        <span className={styles.name}>{item.area}</span>
        <span className={pctClass(acc)}>{acc}%</span>
      </div>
      <div className={styles.bar}>
        <span className={barClass(acc)} style={{ width: `${Math.max(2, acc)}%` }} />
      </div>
      <div className={styles.sub}>{item.total} question{item.total === 1 ? '' : 's'} attempted</div>
    </div>
  );
}

function pctClass(v) {
  if (v < 60) return styles.pctBad;
  if (v < 75) return styles.pctWarn;
  return styles.pctOk;
}
function barClass(v) {
  if (v < 60) return styles.barBad;
  if (v < 75) return styles.barWarn;
  return styles.barOk;
}
